/**
 * Retail collection — Index filters + sort + pagination.
 * Interaction matches the former House collection drawer (surgical AJAX, apply/clear/sort).
 */
(function () {
  'use strict';

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (el.matches(sel)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function refreshLazyImages(container) {
    if (!container) return;
    if (window.lazySizes && window.lazySizes.loader && typeof window.lazySizes.loader.checkElems === 'function') {
      window.lazySizes.loader.checkElems();
      return;
    }
    qsa(container, 'img.lazyload[data-src]').forEach(function (img) {
      if (img.classList.contains('lazyloaded') || img.classList.contains('lazyloading')) return;
      var src = img.getAttribute('data-src');
      if (src) img.setAttribute('src', src);
    });
  }

  function initCollection(root) {
    if (root.__retailCollectionInit) return;
    root.__retailCollectionInit = true;

    /* Drop stuck availability filters only for legacy custom / banner index mode.
       Also drop leftover Ceiling/Table placement values from Room use URL filters. */
    try {
      var engine = root.getAttribute('data-filter-engine') || 'native';
      var bootUrl = new URL(window.location.href);
      var stripped = false;
      var placementVals = { ceiling: true, table: true, wall: true, floor: true };
      Array.prototype.slice.call(bootUrl.searchParams.keys()).forEach(function (key) {
        var keyDown = String(key).toLowerCase();
        if (engine !== 'native' && (key === 'filter.v.availability' || key.indexOf('filter.v.availability.') === 0)) {
          bootUrl.searchParams.delete(key);
          stripped = true;
          return;
        }
        if (keyDown.indexOf('room_use') === -1 && keyDown.indexOf('room-use') === -1) return;
        var kept = bootUrl.searchParams.getAll(key).filter(function (val) {
          return !placementVals[String(val || '').toLowerCase()];
        });
        if (kept.length !== bootUrl.searchParams.getAll(key).length) {
          bootUrl.searchParams.delete(key);
          kept.forEach(function (val) {
            bootUrl.searchParams.append(key, val);
          });
          stripped = true;
        }
      });
      if (stripped) {
        var next = bootUrl.pathname + bootUrl.search + bootUrl.hash;
        if (next !== window.location.pathname + window.location.search + window.location.hash) {
          window.location.replace(next);
          return;
        }
      }
    } catch (e) {
      /* ignore */
    }

    var sectionId = root.getAttribute('data-section-id');
    var ajaxEnabled = root.getAttribute('data-ajax') !== 'false';
    var drawerRoot = qs(root, '[data-retail-drawer-root]');
    var drawer = qs(root, '[data-retail-drawer]');
    var overlay = qs(root, '[data-retail-drawer-overlay]');
    var openBtn = qs(root, '[data-retail-filter-open]');
    var closeBtn = qs(root, '[data-retail-filter-close]');
    var form = qs(root, '[data-retail-facets-form]');
    var applyBtn = qs(root, '[data-retail-apply]');
    var results = qs(root, '[data-retail-results]');
    var sortSelect = qs(root, '[data-retail-sort]');
    var sortToggle = qs(root, '[data-retail-sort-toggle]');
    var sortMenu = qs(root, '[data-retail-sort-menu]');
    var sortLabel = qs(root, '[data-retail-sort-label]');
    var abortController = null;
    var lastFocus = null;

    function getFocusable(container) {
      return qsa(
        container,
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    function setExpanded(open) {
      if (openBtn) openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function openDrawer() {
      if (!drawerRoot) return;
      lastFocus = document.activeElement;
      drawerRoot.hidden = false;
      requestAnimationFrame(function () {
        drawerRoot.classList.add('is-open');
      });
      document.body.classList.add('retail-drawer-open');
      setExpanded(true);
      if (closeBtn) closeBtn.focus();
    }

    function closeDrawer() {
      if (!drawerRoot) return;
      drawerRoot.classList.remove('is-open');
      document.body.classList.remove('retail-drawer-open');
      setExpanded(false);
      window.setTimeout(function () {
        if (!drawerRoot.classList.contains('is-open')) drawerRoot.hidden = true;
      }, 280);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function closeSortMenu() {
      if (!sortMenu || !sortToggle) return;
      sortMenu.classList.remove('is-open');
      sortMenu.hidden = true;
      sortToggle.setAttribute('aria-expanded', 'false');
    }

    function openSortMenu() {
      if (!sortMenu || !sortToggle) return;
      sortMenu.hidden = false;
      sortMenu.classList.add('is-open');
      sortToggle.setAttribute('aria-expanded', 'true');
    }

    function getCollectionBasePath() {
      var configured = root.getAttribute('data-collection-url');
      if (configured) return configured.split('?')[0];
      var parts = window.location.pathname.replace(/\/+$/, '').split('/');
      if (parts.length >= 3 && parts[1] === 'collections') {
        return '/' + parts[1] + '/' + parts[2];
      }
      return window.location.pathname;
    }

    function buildUrlFromForm(extra) {
      var basePath = getCollectionBasePath();
      var url = new URL(basePath, window.location.origin);
      /* Preserve non-filter params from the current location when already on this collection */
      try {
        var current = new URL(window.location.href);
        var onCollection =
          current.pathname.replace(/\/+$/, '') === basePath.replace(/\/+$/, '') ||
          current.pathname.indexOf(basePath.replace(/\/+$/, '') + '/') === 0;
        if (onCollection) {
          current.searchParams.forEach(function (value, key) {
            if (key.indexOf('filter.') === 0 || key === 'page' || key === 'section_id' || key === 'sections') return;
            if (key === 'sort_by') return;
            url.searchParams.set(key, value);
          });
        }
      } catch (e) {
        /* ignore */
      }

      /* Drawer sort radios → hidden sort select before reading FormData */
      var drawerSortInput = form ? qs(form, 'input[name="retail_filter_sort"]:checked') : null;
      if (drawerSortInput && sortSelect && drawerSortInput.value) {
        sortSelect.value = drawerSortInput.value;
        var sortFacet = closest(drawerSortInput, '[data-retail-facet-sort]');
        var valueEl = sortFacet ? qs(sortFacet, '[data-retail-sort-value]') : null;
        if (valueEl) {
          valueEl.textContent =
            drawerSortInput.getAttribute('data-sort-label') || drawerSortInput.value;
        }
      }

      if (form) {
        var fd = new FormData(form);
        fd.forEach(function (value, key) {
          if (value === '' || value == null) return;
          /* Client-only drawer fields — not Shopify collection URL params */
          if (key.indexOf('retail_filter_') === 0) return;
          if (key === 'retail_native_price_chip') return;
          if (url.searchParams.has(key)) {
            url.searchParams.append(key, value);
          } else {
            url.searchParams.set(key, value);
          }
        });
      }

      var readyShip = qs(root, '[data-retail-ready-ship]');
      if (readyShip && readyShip.checked && readyShip.name) {
        url.searchParams.set(readyShip.name, readyShip.value);
      }

      if (sortSelect && sortSelect.value) {
        url.searchParams.set('sort_by', sortSelect.value);
      }

      if (extra) {
        Object.keys(extra).forEach(function (k) {
          if (extra[k] == null || extra[k] === '') url.searchParams.delete(k);
          else url.searchParams.set(k, extra[k]);
        });
      }

      return url;
    }

    function updateApplyLabel(count) {
      if (!applyBtn) return;
      var mobileLabel = applyBtn.getAttribute('data-mobile-label');
      if (mobileLabel && window.matchMedia('(max-width: 767.98px)').matches) {
        applyBtn.textContent = mobileLabel;
        return;
      }
      var label = applyBtn.getAttribute('data-label-template') || 'Apply';
      if (label.indexOf('__COUNT__') === -1 && !/\{\{\s*count\s*\}\}/.test(label)) {
        applyBtn.textContent = label;
        return;
      }
      applyBtn.textContent = label
        .replace(/__COUNT__/g, String(count))
        .replace(/\{\{\s*count\s*\}\}/g, String(count));
    }

    function updateDrawerFacetLabels() {
      if (!form) return;
      var sortInput = qs(form, 'input[name="retail_filter_sort"]:checked');
      var sortValueEl = qs(form, '[data-retail-sort-value]');
      if (sortInput && sortValueEl) {
        sortValueEl.textContent =
          sortInput.getAttribute('data-sort-label') || sortInput.value || 'Most relevant';
      }

      var priceValueEl = qs(form, '[data-retail-price-value]');
      if (!priceValueEl) return;
      var checked = qsa(form, 'input[name="retail_filter_price"]:checked');
      if (!checked.length) {
        priceValueEl.textContent =
          priceValueEl.getAttribute('data-default-value') || '£0 – £1000+';
        return;
      }
      if (checked.length === 1) {
        var chip = closest(checked[0], '.retail-price-chip');
        var chipText = chip ? qs(chip, '.retail-price-chip__text') : null;
        if (chipText) {
          priceValueEl.textContent = chipText.textContent.replace(/\s*\([^)]*\)\s*$/, '').trim();
        } else {
          priceValueEl.textContent = checked[0].value;
        }
        return;
      }
      priceValueEl.textContent = checked.length + ' selected';
    }

    function setLoading(on) {
      root.classList.toggle('is-loading', !!on);
      if (applyBtn) applyBtn.disabled = !!on;
    }

    function syncFilterTriggerLabel(nextRoot) {
      if (!openBtn || !nextRoot) return;
      var nextTrigger = nextRoot.querySelector('[data-retail-filter-open]');
      if (!nextTrigger) return;
      var nextText = nextTrigger.querySelector('.retail-filters-btn__label, .index-field-text, .retail-index-field-text');
      var curText = openBtn.querySelector('.retail-filters-btn__label, .index-field-text, .retail-index-field-text');
      if (curText && nextText) {
        curText.textContent = nextText.textContent.trim();
      }
    }

    function syncSortUI(nextRoot) {
      if (!sortSelect) return;
      var nextSort = nextRoot ? nextRoot.querySelector('[data-retail-sort]') : null;
      if (nextSort && nextSort.value) sortSelect.value = nextSort.value;

      var nextLabel = nextRoot ? nextRoot.querySelector('[data-retail-sort-label]') : null;
      if (sortLabel && nextLabel) sortLabel.textContent = nextLabel.textContent.trim();

      qsa(root, '[data-retail-sort-option]').forEach(function (btn) {
        var on = btn.getAttribute('data-sort') === sortSelect.value;
        btn.classList.toggle('active', on);
        btn.classList.toggle('is-active', on);
      });
    }

    function syncClearButton(nextRoot) {
      var clearBtn = qs(root, '.retail-controls [data-retail-clear-all], .retail-index [data-retail-clear-all]');
      var nextClear = nextRoot
        ? nextRoot.querySelector('.retail-controls [data-retail-clear-all], .retail-index [data-retail-clear-all]')
        : null;
      if (!clearBtn || !nextClear) return;
      var hidden = nextClear.hasAttribute('hidden') || nextClear.classList.contains('is-hidden');
      clearBtn.classList.toggle('is-hidden', hidden);
      if (hidden) clearBtn.setAttribute('hidden', '');
      else clearBtn.removeAttribute('hidden');
    }

    function syncClearAllFooter(nextRoot) {
      var footer = qs(root, '.retail-drawer__footer');
      if (!footer || !nextRoot) return;
      var nextFooter = nextRoot.querySelector('.retail-drawer__footer');
      if (!nextFooter) return;
      var curClear = footer.querySelector('[data-retail-clear-all]');
      var nextClear = nextFooter.querySelector('[data-retail-clear-all]');
      if (nextClear && !curClear) {
        footer.appendChild(nextClear.cloneNode(true));
      } else if (!nextClear && curClear) {
        curClear.remove();
      }
    }

    function syncReadyShip(nextRoot) {
      var readyShip = qs(root, '[data-retail-ready-ship]');
      if (!readyShip || !nextRoot) return;
      var nextReady = nextRoot.querySelector('[data-retail-ready-ship]');
      if (nextReady) {
        readyShip.checked = nextReady.checked;
      }
      var label = closest(readyShip, '.retail-ready-toggle');
      if (label) {
        label.classList.toggle('is-active', !!readyShip.checked);
      }
    }

    function syncTagPills(nextRoot) {
      var curPills = qs(root, '[data-retail-tag-pills]');
      var nextPills = nextRoot ? nextRoot.querySelector('[data-retail-tag-pills]') : null;
      if (curPills && nextPills) {
        curPills.innerHTML = nextPills.innerHTML;
      }
    }

    function syncBreadcrumbCount(nextRoot) {
      var curCount = qs(root, '.retail-soho-breadcrumb__count');
      var nextCountEl = nextRoot ? nextRoot.querySelector('.retail-soho-breadcrumb__count') : null;
      if (curCount && nextCountEl) {
        curCount.textContent = nextCountEl.textContent;
      }
    }

    function setExclusiveTagSelection(selectedPill) {
      /* selectedPill = tag pill element, or null/All for cleared state */
      var selectingAll =
        !selectedPill ||
        selectedPill.hasAttribute('data-retail-tag-all') ||
        selectedPill.classList.contains('retail-soho-pill--all');

      qsa(root, '[data-retail-tag-pill]').forEach(function (pill) {
        var isAll =
          pill.hasAttribute('data-retail-tag-all') || pill.classList.contains('retail-soho-pill--all');

        if (isAll) {
          pill.classList.remove('is-active');
          pill.classList.add('is-inactive');
          if (selectingAll) {
            pill.classList.add('is-current');
            pill.setAttribute('aria-current', 'true');
            pill.setAttribute('aria-disabled', 'true');
            pill.setAttribute('tabindex', '-1');
          } else {
            pill.classList.remove('is-current');
            pill.removeAttribute('aria-current');
            pill.removeAttribute('aria-disabled');
            pill.removeAttribute('tabindex');
          }
          return;
        }

        var on = !selectingAll && pill === selectedPill;
        pill.classList.toggle('is-active', on);
        if (on) pill.setAttribute('aria-current', 'true');
        else pill.removeAttribute('aria-current');
      });
    }

    function setExclusiveKindSelection(selectedPill) {
      var selectingAll =
        !selectedPill ||
        selectedPill.hasAttribute('data-retail-tag-all') ||
        selectedPill.classList.contains('retail-soho-pill--all') ||
        !(selectedPill.getAttribute('data-retail-kind') || '');

      qsa(root, '[data-retail-kind-pill]').forEach(function (pill) {
        var isAll =
          pill.hasAttribute('data-retail-tag-all') ||
          pill.classList.contains('retail-soho-pill--all') ||
          !(pill.getAttribute('data-retail-kind') || '');

        if (isAll) {
          pill.classList.remove('is-active');
          pill.classList.add('is-inactive');
          if (selectingAll) {
            pill.classList.add('is-current');
            pill.setAttribute('aria-current', 'true');
          } else {
            pill.classList.remove('is-current');
            pill.removeAttribute('aria-current');
          }
          return;
        }

        var on = !selectingAll && pill === selectedPill;
        pill.classList.toggle('is-active', on);
        if (on) pill.setAttribute('aria-current', 'true');
        else pill.removeAttribute('aria-current');
      });
    }

    function getSelectedKind() {
      var active = qs(root, '[data-retail-kind-pill].is-active');
      return active ? (active.getAttribute('data-retail-kind') || '').trim() : '';
    }

    function replaceFromSectionHtml(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.querySelector('[data-retail-collection][data-section-id="' + sectionId + '"]');
      if (!next) {
        var sectionEl = doc.getElementById('shopify-section-' + sectionId);
        if (sectionEl) next = sectionEl.querySelector('[data-retail-collection]');
      }
      if (!next) {
        next = doc.querySelector('[data-retail-collection][data-section-type="retail-collection"]');
      }
      if (!next) {
        next = doc.querySelector('[data-retail-collection]');
      }
      if (!next || !results) return false;

      var nextResults = next.querySelector('[data-retail-results]');
      var nextActive = next.querySelector('[data-retail-active-filters]');
      var nextForm = next.querySelector('[data-retail-facets-form]');
      var nextCount = next.getAttribute('data-result-count');
      var nextToolbar = next.querySelector('[data-retail-toolbar]');
      var curToolbar = qs(root, '[data-retail-toolbar]');

      if (nextResults) results.innerHTML = nextResults.innerHTML;
      var active = qs(root, '[data-retail-active-filters]');
      if (active && nextActive) active.innerHTML = nextActive.innerHTML;
      if (form && nextForm && !hasCustomFacets()) form.innerHTML = nextForm.innerHTML;

      var curPager = qs(root, '[data-retail-pager]');
      var nextPager = next.querySelector('[data-retail-pager]');
      if (curPager && nextPager) {
        curPager.innerHTML = nextPager.innerHTML;
      } else if (curPager && !nextPager) {
        curPager.innerHTML = '';
      }

      if (curToolbar && nextToolbar) {
        var nextMeta = nextToolbar.querySelector('[data-retail-result-meta]');
        var curMeta = curToolbar.querySelector('[data-retail-result-meta]');
        if (curMeta && nextMeta) curMeta.textContent = nextMeta.textContent.replace(/\s+/g, ' ').trim();
        var nextSort = nextToolbar.querySelector('[data-retail-sort]');
        if (sortSelect && nextSort) sortSelect.innerHTML = nextSort.innerHTML;
      }
      if (typeof nextCount !== 'undefined' && nextCount !== null) {
        root.setAttribute('data-result-count', nextCount);
        updateApplyLabel(nextCount);
        var meta = qs(root, '[data-retail-result-meta]');
        var nextMetaEl = next.querySelector('[data-retail-result-meta]');
        if (meta && nextMetaEl) {
          meta.textContent = nextMetaEl.textContent.replace(/\s+/g, ' ').trim();
        } else if (meta) {
          meta.textContent = nextCount + (Number(nextCount) === 1 ? ' product' : ' products');
        }
      }

      root.setAttribute('data-sort-by', next.getAttribute('data-sort-by') || '');
      syncTagPills(next);
      syncBreadcrumbCount(next);
      syncFilterTriggerLabel(next);
      syncSortUI(next);
      syncClearButton(next);
      syncClearAllFooter(next);
      syncReadyShip(next);
      bindFacetAccordions();
      refreshLazyImages(results);
      return true;
    }

    function scrollToResults() {
      if (!results) return;
      var top = results.getBoundingClientRect().top + window.pageYOffset;
      var offset =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tcn-header-offset')) || 130;
      window.scrollTo({ top: Math.max(0, top - offset - 16), behavior: 'smooth' });
    }

    function attachTemplateView(url) {
      var view = root.getAttribute('data-template-view');
      if (view) url.searchParams.set('view', view);
      return url;
    }

    function fetchSection(url, pushState, options) {
      options = options || {};
      attachTemplateView(url);
      if (!ajaxEnabled) {
        window.location.href = url.pathname + url.search;
        return Promise.resolve(false);
      }
      if (abortController) abortController.abort();
      abortController = new AbortController();
      setLoading(true);

      var collectionPath = getCollectionBasePath().replace(/\/+$/, '');
      var requestPath = url.pathname.replace(/\/+$/, '');
      var onCollectionContext =
        requestPath === collectionPath || requestPath.indexOf(collectionPath + '/') === 0;
      var pageOnCollection =
        window.location.pathname.replace(/\/+$/, '') === collectionPath ||
        window.location.pathname.indexOf(collectionPath + '/') === 0;

      var fetchUrl = new URL(url.toString());
      /* Prefer instance section_id when this section lives on the collection template.
         Otherwise render the reusable section file against the collection URL. */
      if (pageOnCollection && onCollectionContext && sectionId) {
        fetchUrl.searchParams.set('section_id', sectionId);
        fetchUrl.searchParams.delete('sections');
      } else {
        fetchUrl.searchParams.delete('section_id');
        fetchUrl.searchParams.set('sections', 'retail-collection');
      }

      return fetch(fetchUrl.toString(), {
        signal: abortController.signal,
        headers: { Accept: 'application/json, text/html' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Section render failed');
          var type = (res.headers.get('Content-Type') || '').toLowerCase();
          if (type.indexOf('application/json') !== -1 || fetchUrl.searchParams.has('sections')) {
            return res.json().then(function (json) {
              var html =
                (json && (json['retail-collection'] || json[sectionId])) ||
                (json && typeof json === 'object'
                  ? json[Object.keys(json)[0]]
                  : null);
              if (!html) throw new Error('Empty section payload');
              return html;
            });
          }
          return res.text();
        })
        .then(function (html) {
          var ok = replaceFromSectionHtml(html);
          if (pushState) {
            var clean = new URL(url.toString());
            clean.searchParams.delete('section_id');
            clean.searchParams.delete('sections');
            history.pushState({ retailFilters: true }, '', clean.pathname + clean.search);
          } else if (options.replaceState) {
            var cleanReplace = new URL(url.toString());
            cleanReplace.searchParams.delete('section_id');
            cleanReplace.searchParams.delete('sections');
            history.replaceState({ retailFilters: true }, '', cleanReplace.pathname + cleanReplace.search);
          }
          if (ok && options.scroll) scrollToResults();
          return ok;
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return false;
          window.location.href = url.pathname + url.search;
          return false;
        })
        .finally(function () {
          setLoading(false);
        });
    }

    function getCheckedValues(name) {
      if (!form) return [];
      return qsa(form, 'input[name="' + name + '"]:checked').map(function (input) {
        return input.value;
      });
    }

    function getIndexWork() {
      var active = qs(root, '[data-retail-index-work-option].active, [data-retail-index-work-option].is-active');
      return active ? active.getAttribute('data-work') : '';
    }

    function getIndexType() {
      var active = qs(root, '[data-retail-index-type-option].active, [data-retail-index-type-option].is-active');
      return active ? active.getAttribute('data-type') : '';
    }

    function getIndexRoom() {
      var active = qs(root, '[data-retail-index-room-option].active, [data-retail-index-room-option].is-active');
      return active ? active.getAttribute('data-room') : '';
    }

    function closeIndexMenus() {
      qsa(root, '[data-retail-index-work-menu], [data-retail-index-type-menu], [data-retail-index-room-menu]').forEach(function (menu) {
        menu.classList.remove('is-open');
        menu.setAttribute('hidden', '');
      });
      qsa(root, '[data-retail-index-work-toggle], [data-retail-index-type-toggle], [data-retail-index-room-toggle]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
    }

    function updateIndexClearVisibility() {
      var clearBtn = qs(root, '.retail-controls [data-retail-clear-all], .retail-index [data-retail-clear-all]');
      if (!clearBtn) return;
      var drawerActive =
        getCheckedValues('retail_filter_type').length +
          getCheckedValues('retail_filter_brand').length +
          getCheckedValues('retail_filter_room').length +
          getCheckedValues('retail_filter_material').length +
          getCheckedValues('retail_filter_colour').length +
          (form ? qsa(form, 'input[name="retail_filter_price"]:checked').length : 0) >
        0;
      var indexActive = !!(getIndexWork() || getIndexType() || getIndexRoom());
      var tagActive = !!qs(root, '[data-retail-tag-pill].is-active');
      var show = drawerActive || indexActive || tagActive;
      clearBtn.classList.toggle('is-hidden', !show);
      if (show) clearBtn.removeAttribute('hidden');
      else clearBtn.setAttribute('hidden', '');
    }

    function resetIndexDropdowns() {
      qsa(root, '[data-retail-index-work-option], [data-retail-index-type-option], [data-retail-index-room-option]').forEach(function (btn) {
        btn.classList.remove('active', 'is-active');
      });
      var workLabel = qs(root, '[data-retail-index-work-label]');
      var typeLabel = qs(root, '[data-retail-index-type-label]');
      var roomLabel = qs(root, '[data-retail-index-room-label]');
      if (workLabel) workLabel.textContent = workLabel.getAttribute('data-default-label') || 'All works';
      if (typeLabel) typeLabel.textContent = typeLabel.getAttribute('data-default-label') || 'All types';
      if (roomLabel) roomLabel.textContent = roomLabel.getAttribute('data-default-label') || 'All rooms';
      closeIndexMenus();
    }

    function matchesIndexFilters(item) {
      var selectedWork = getIndexWork();
      var selectedType = getIndexType();
      var selectedRoom = getIndexRoom();
      if (!selectedWork && !selectedType && !selectedRoom) return true;

      var itemWork = item.getAttribute('data-work') || '';
      var itemType = item.getAttribute('data-type') || '';
      var itemRoom = item.getAttribute('data-retail-room') || '';

      var workMatch = !selectedWork || itemWork === selectedWork;
      var typeMatch = !selectedType || itemType === selectedType;
      var roomMatch = !selectedRoom || itemRoom === selectedRoom;

      return workMatch && typeMatch && roomMatch;
    }

    function hasCustomFacets() {
      if (root.getAttribute('data-filter-engine') === 'native') return false;
      /* Client-side index filters (All works / All types) — used by banner template */
      if (qs(root, '[data-retail-index-work], [data-retail-index-type], [data-retail-index-room]')) return true;
      return !!(
        form &&
        (form.getAttribute('data-retail-custom-filter') === 'true' || qs(form, '[data-retail-custom-facets]'))
      );
    }

    function matchesAny(csv, selected) {
      if (!selected.length) return true;
      var values = (csv || '')
        .split(',')
        .map(function (v) {
          return v.trim();
        })
        .filter(Boolean);
      if (!values.length) return false;
      for (var i = 0; i < selected.length; i++) {
        if (values.indexOf(selected[i]) !== -1) return true;
      }
      return false;
    }

    function applyCustomFilters() {
      var types = getCheckedValues('retail_filter_type');
      var brands = getCheckedValues('retail_filter_brand');
      var rooms = getCheckedValues('retail_filter_room');
      var materials = getCheckedValues('retail_filter_material');
      var colours = getCheckedValues('retail_filter_colour');
      var priceInputs = form ? qsa(form, 'input[name="retail_filter_price"]:checked') : [];
      var sortInput = form ? qs(form, 'input[name="retail_filter_sort"]:checked') : null;

      var items = qsa(root, '[data-retail-results] .product-item');
      var visible = 0;

      items.forEach(function (item) {
        var ok = matchesIndexFilters(item);
        var kind = getSelectedKind();
        if (ok && kind) {
          ok = (item.getAttribute('data-retail-kind') || '').trim() === kind;
        }
        if (ok && types.length) {
          ok =
            matchesAny(item.getAttribute('data-type'), types) ||
            matchesAny(item.getAttribute('data-retail-kind'), types) ||
            matchesAny(item.getAttribute('data-retail-tags'), types);
        }
        if (ok && brands.length) {
          ok = matchesAny(item.getAttribute('data-retail-brand'), brands);
        }
        if (ok && rooms.length) {
          ok = matchesAny(item.getAttribute('data-retail-room'), rooms);
        }
        if (ok && materials.length) {
          ok = matchesAny(item.getAttribute('data-retail-material'), materials);
        }
        if (ok && colours.length) {
          ok = matchesAny(item.getAttribute('data-retail-colour'), colours);
        }
        if (ok && priceInputs.length) {
          var price = parseFloat(item.getAttribute('data-retail-price') || '');
          if (isNaN(price)) {
            ok = false;
          } else {
            var priceOk = false;
            priceInputs.forEach(function (input) {
              var mode = input.getAttribute('data-price-mode');
              var value = parseFloat(input.getAttribute('data-price-value') || '');
              if (isNaN(value)) return;
              if (mode === 'over') {
                if (price > value) priceOk = true;
              } else if (price <= value) {
                priceOk = true;
              }
            });
            ok = priceOk;
          }
        }

        item.style.display = ok ? '' : 'none';
        if (ok) visible += 1;
      });

      var activeCount =
        types.length + brands.length + rooms.length + materials.length + colours.length + priceInputs.length;
      if (getIndexWork()) activeCount += 1;
      if (getIndexType()) activeCount += 1;

      root.setAttribute('data-result-count', String(visible));
      updateApplyLabel(visible);

      var meta = qs(root, '[data-retail-result-meta]');
      if (meta) {
        meta.textContent = visible + (visible === 1 ? ' product' : ' products');
      }

      var filterText = openBtn ? qs(openBtn, '.retail-filters-btn__label, .index-field-text, .retail-index-field-text') : null;
      if (filterText) {
        var drawerCount =
          types.length + brands.length + rooms.length + materials.length + colours.length + priceInputs.length;
        filterText.textContent = drawerCount > 0 ? 'Filters (' + drawerCount + ')' : 'Filters';
      }

      updateIndexClearVisibility();

      qsa(form || root, '.retail-price-chip').forEach(function (chip) {
        var input = qs(chip, 'input');
        chip.classList.toggle('is-active', !!(input && input.checked));
      });
      qsa(form || root, '.retail-sort-list .retail-facet__option').forEach(function (opt) {
        var input = qs(opt, 'input');
        opt.classList.toggle('is-active', !!(input && input.checked));
      });

      updateDrawerFacetLabels();

      var empty = qs(root, '[data-retail-empty]');
      var grid = qs(root, '[data-retail-results] .product-grid-view');
      if (items.length) {
        if (empty) {
          if (visible === 0) {
            empty.hidden = false;
            empty.innerHTML =
              '<p>No products match these filters.</p><button type="button" class="index-field" data-retail-clear-all><span class="index-field-text">Clear filters</span></button>';
          } else {
            empty.hidden = true;
          }
        }
        if (grid) grid.style.display = visible === 0 ? 'none' : '';
      }

      if (sortInput && sortSelect && sortInput.value && sortInput.value !== sortSelect.value) {
        var sortLabel = sortInput.getAttribute('data-sort-label') || sortInput.value;
        setSort(sortInput.value, sortLabel, { skipCloseSort: true, after: function () {
          applyCustomFiltersDomOnly();
        }});
        return visible;
      }

      scrollToResults();
      return visible;
    }

    function applyCustomFiltersDomOnly() {
      /* Re-run visibility after sort AJAX without recursive sort fetch */
      var sortInput = form ? qs(form, 'input[name="retail_filter_sort"]:checked') : null;
      if (sortInput && sortSelect) sortSelect.value = sortInput.value;
      var types = getCheckedValues('retail_filter_type');
      var brands = getCheckedValues('retail_filter_brand');
      var rooms = getCheckedValues('retail_filter_room');
      var materials = getCheckedValues('retail_filter_material');
      var colours = getCheckedValues('retail_filter_colour');
      var priceInputs = form ? qsa(form, 'input[name="retail_filter_price"]:checked') : [];
      var items = qsa(root, '[data-retail-results] .product-item');
      var visible = 0;
      items.forEach(function (item) {
        var ok = matchesIndexFilters(item);
        var kind = getSelectedKind();
        if (ok && kind) ok = (item.getAttribute('data-retail-kind') || '').trim() === kind;
        if (types.length) {
          ok =
            ok &&
            (matchesAny(item.getAttribute('data-type'), types) ||
              matchesAny(item.getAttribute('data-retail-kind'), types) ||
              matchesAny(item.getAttribute('data-retail-tags'), types));
        }
        if (ok && brands.length) ok = matchesAny(item.getAttribute('data-retail-brand'), brands);
        if (ok && rooms.length) ok = matchesAny(item.getAttribute('data-retail-room'), rooms);
        if (ok && materials.length) ok = matchesAny(item.getAttribute('data-retail-material'), materials);
        if (ok && colours.length) ok = matchesAny(item.getAttribute('data-retail-colour'), colours);
        if (ok && priceInputs.length) {
          var price = parseFloat(item.getAttribute('data-retail-price') || '');
          if (isNaN(price)) ok = false;
          else {
            var priceOk = false;
            priceInputs.forEach(function (input) {
              var mode = input.getAttribute('data-price-mode');
              var value = parseFloat(input.getAttribute('data-price-value') || '');
              if (isNaN(value)) return;
              if (mode === 'over') {
                if (price > value) priceOk = true;
              } else if (price <= value) priceOk = true;
            });
            ok = priceOk;
          }
        }
        item.style.display = ok ? '' : 'none';
        if (ok) visible += 1;
      });
      root.setAttribute('data-result-count', String(visible));
      updateApplyLabel(visible);
      updateIndexClearVisibility();
      var meta = qs(root, '[data-retail-result-meta]');
      if (meta) meta.textContent = visible + (visible === 1 ? ' product' : ' products');
      scrollToResults();
    }

    function clearCustomFilters() {
      if (form) {
        qsa(form, 'input[type="checkbox"]').forEach(function (input) {
          input.checked = false;
        });
        qsa(form, 'input[type="radio"]').forEach(function (input) {
          input.checked = input.name === 'retail_filter_sort' && input.value === 'manual';
        });
        qsa(form, 'input[type="number"], input[type="text"]').forEach(function (input) {
          input.value = '';
        });
        qsa(form, '.retail-price-chip').forEach(function (chip) {
          chip.classList.remove('is-active');
        });
        var mostRelevant = qs(form, 'input[name="retail_filter_sort"][value="manual"]');
        if (mostRelevant) mostRelevant.checked = true;
      }
      if (typeof resetIndexDropdowns === 'function') {
        try {
          resetIndexDropdowns();
        } catch (err) {}
      }
      var readyShip = qs(root, '[data-retail-ready-ship]');
      if (readyShip) {
        readyShip.checked = false;
        var readyLabel = closest(readyShip, '.retail-ready-toggle');
        if (readyLabel) readyLabel.classList.remove('is-active');
      }
      var allKindPill = qs(root, '[data-retail-kind-pill][data-retail-tag-all]');
      if (allKindPill) setExclusiveKindSelection(allKindPill);
      updateDrawerFacetLabels();
      applyCustomFilters();
    }

    function applyFilters(pushState) {
      if (hasCustomFacets()) {
        applyCustomFilters();
        closeDrawer();
        return;
      }
      var url = buildUrlFromForm();
      url.searchParams.delete('page');
      fetchSection(url, pushState !== false, { scroll: true });
      closeDrawer();
    }

    function setSort(value, label, options) {
      options = options || {};
      if (!sortSelect || !value) return;
      sortSelect.value = value;
      if (sortLabel && label) sortLabel.textContent = label;
      qsa(root, '[data-retail-sort-option]').forEach(function (btn) {
        var on = btn.getAttribute('data-sort') === value;
        btn.classList.toggle('active', on);
        btn.classList.toggle('is-active', on);
      });
      var drawerSort = form ? qs(form, 'input[name="retail_filter_sort"][value="' + value + '"]') : null;
      if (drawerSort) drawerSort.checked = true;

      var url = buildUrlFromForm();
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      fetchSection(url, true, { scroll: !options.after }).then(function (ok) {
        if (typeof options.after === 'function') options.after(ok);
      });
      if (!options.skipCloseSort) closeSortMenu();
    }

    function bindFacetAccordions() {
      qsa(root, '[data-retail-facet]').forEach(function (facet) {
        var toggle = qs(facet, '[data-retail-facet-toggle]');
        var panel = qs(facet, '.retail-facet__panel');
        if (!toggle || toggle.__bound) return;
        toggle.__bound = true;
        toggle.addEventListener('click', function () {
          var open = !facet.classList.contains('is-open');
          facet.classList.toggle('is-open', open);
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (panel) {
            if (open) panel.removeAttribute('hidden');
            else panel.setAttribute('hidden', '');
          }
        });
      });
    }

    /* Leave Featured/manual — removed: collection-template-banner shows all products with collection default sort */

    if (openBtn) openBtn.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    function bindIndexDropdown(toggleSel, menuSel, optionSel, labelSel, defaultLabel, attrName) {
      var toggle = qs(root, toggleSel);
      var menu = qs(root, menuSel);
      if (!toggle || !menu) return;

      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var isOpen = toggle.getAttribute('aria-expanded') === 'true';
        closeSortMenu();
        closeIndexMenus();
        if (!isOpen) {
          toggle.setAttribute('aria-expanded', 'true');
          menu.classList.add('is-open');
          menu.removeAttribute('hidden');
        }
      });

      qsa(menu, optionSel).forEach(function (item) {
        item.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          qsa(menu, optionSel).forEach(function (btn) {
            btn.classList.remove('active', 'is-active');
          });
          item.classList.add('active', 'is-active');
          var label = qs(root, labelSel);
          var value = item.getAttribute(attrName);
          if (label) {
            var fallback = label.getAttribute('data-default-label') || defaultLabel;
            label.textContent = value ? item.textContent.trim() : fallback;
          }
          closeIndexMenus();
          applyCustomFilters();
        });
      });
    }

    bindIndexDropdown(
      '[data-retail-index-work-toggle]',
      '[data-retail-index-work-menu]',
      '[data-retail-index-work-option]',
      '[data-retail-index-work-label]',
      'All collections',
      'data-work'
    );
    bindIndexDropdown(
      '[data-retail-index-type-toggle]',
      '[data-retail-index-type-menu]',
      '[data-retail-index-type-option]',
      '[data-retail-index-type-label]',
      'All types',
      'data-type'
    );

    bindIndexDropdown(
      '[data-retail-index-room-toggle]',
      '[data-retail-index-room-menu]',
      '[data-retail-index-room-option]',
      '[data-retail-index-room-label]',
      'All rooms',
      'data-room'
    );

    document.addEventListener('click', function (e) {
      if (!closest(e.target, '[data-retail-index-work], [data-retail-index-type], [data-retail-index-room]')) {
        closeIndexMenus();
      }
    });

    if (sortToggle && sortMenu) {
      sortToggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (sortMenu.classList.contains('is-open')) closeSortMenu();
        else openSortMenu();
      });

      sortMenu.addEventListener('click', function (e) {
        var option = closest(e.target, '[data-retail-sort-option]');
        if (!option) return;
        e.preventDefault();
        setSort(option.getAttribute('data-sort'), option.textContent.trim());
      });

      document.addEventListener('click', function (e) {
        if (!sortMenu.classList.contains('is-open')) return;
        if (closest(e.target, '[data-retail-sort-wrap]')) return;
        closeSortMenu();
      });
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var opt = sortSelect.options[sortSelect.selectedIndex];
        setSort(sortSelect.value, opt ? opt.textContent.trim() : sortSelect.value, { skipCloseSort: true });
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (sortMenu && sortMenu.classList.contains('is-open')) {
          closeSortMenu();
          return;
        }
        if (!drawerRoot || drawerRoot.hidden) return;
        e.preventDefault();
        closeDrawer();
        return;
      }
      if (e.key === 'Tab' && drawer && drawerRoot && !drawerRoot.hidden) {
        var focusables = getFocusable(drawer);
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    if (applyBtn) {
      applyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        applyFilters(true);
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        applyFilters(true);
      });

      form.addEventListener('change', function (e) {
        var target = e.target;
        if (!target || !target.matches) return;
        if (
          target.matches(
            'input[name="retail_filter_sort"], input[name="retail_filter_price"], input[name="retail_filter_type"], input[name="retail_filter_brand"], input[name="retail_filter_room"]'
          )
        ) {
          updateDrawerFacetLabels();
          if (hasCustomFacets()) applyCustomFilters();
        }
      });
    }

    root.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || !target.matches) return;
      if (target.matches('[data-retail-ready-ship]')) {
        var label = closest(target, '.retail-ready-toggle');
        if (label) label.classList.toggle('is-active', !!target.checked);
        applyFilters(true);
      }
    });

    root.addEventListener('click', function (e) {
      var pageLink = closest(e.target, '[data-retail-results] .pagination a, [data-retail-pagination] a');
      if (pageLink) {
        e.preventDefault();
        var pageHref = pageLink.getAttribute('href');
        if (!pageHref || pageHref === '#') return;
        fetchSection(new URL(pageHref, window.location.origin), true, { scroll: true });
        return;
      }

      var moreBtn = closest(e.target, '[data-retail-more]');
      if (moreBtn) {
        e.preventDefault();
        if (moreBtn.disabled || moreBtn.getAttribute('aria-busy') === 'true') return;
        var nextHref = moreBtn.getAttribute('data-retail-next');
        if (!nextHref || nextHref === '#') return;

        moreBtn.setAttribute('aria-busy', 'true');
        moreBtn.disabled = true;
        root.classList.add('is-loading');

        var nextUrl = new URL(nextHref, window.location.origin);
        var collectionPath = getCollectionBasePath().replace(/\/+$/, '');
        var requestPath = nextUrl.pathname.replace(/\/+$/, '');
        var onCollectionContext =
          requestPath === collectionPath || requestPath.indexOf(collectionPath + '/') === 0;
        var pageOnCollection =
          window.location.pathname.replace(/\/+$/, '') === collectionPath ||
          window.location.pathname.indexOf(collectionPath + '/') === 0;

        var fetchUrl = new URL(nextUrl.toString());
        if (pageOnCollection && onCollectionContext && sectionId) {
          fetchUrl.searchParams.set('section_id', sectionId);
          fetchUrl.searchParams.delete('sections');
        } else {
          fetchUrl.searchParams.delete('section_id');
          fetchUrl.searchParams.set('sections', 'retail-collection');
        }

        fetch(fetchUrl.toString(), {
          headers: { Accept: 'application/json, text/html' },
          credentials: 'same-origin'
        })
          .then(function (res) {
            if (!res.ok) throw new Error('More results failed');
            var type = (res.headers.get('Content-Type') || '').toLowerCase();
            if (type.indexOf('application/json') !== -1 || fetchUrl.searchParams.has('sections')) {
              return res.json().then(function (json) {
                var html =
                  (json && (json['retail-collection'] || json[sectionId])) ||
                  (json && typeof json === 'object' ? json[Object.keys(json)[0]] : null);
                if (!html) throw new Error('Empty section payload');
                return html;
              });
            }
            return res.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var nextRoot =
              doc.querySelector('[data-retail-collection][data-section-id="' + sectionId + '"]') ||
              doc.querySelector('[data-retail-collection]');
            if (!nextRoot) throw new Error('No section root');

            var gridRow =
              qs(root, '[data-retail-results] .product-grid-view .row') ||
              qs(root, '[data-retail-results] .row');
            var nextItems = qsa(nextRoot, '[data-retail-results] .product-item');
            if (gridRow && nextItems.length) {
              nextItems.forEach(function (item) {
                var id = item.getAttribute('data-product-id');
                if (id && qs(root, '[data-retail-results] .product-item[data-product-id="' + id + '"]')) {
                  return;
                }
                gridRow.appendChild(item);
              });
              refreshLazyImages(gridRow);
            }

            var shownEl = qs(root, '[data-retail-more-shown]');
            var totalEl = qs(root, '[data-retail-more-total]');
            var shownCount = qsa(root, '[data-retail-results] .product-item').length;
            var totalCount =
              Number(
                (totalEl && totalEl.textContent) ||
                  nextRoot.getAttribute('data-result-count') ||
                  root.getAttribute('data-result-count') ||
                  0
              ) || 0;
            if (shownEl) shownEl.textContent = String(shownCount);
            if (totalEl && totalCount) totalEl.textContent = String(totalCount);

            var nextMore = nextRoot.querySelector('[data-retail-more]');
            if (nextMore && nextMore.getAttribute('data-retail-next')) {
              moreBtn.setAttribute('data-retail-next', nextMore.getAttribute('data-retail-next'));
              moreBtn.disabled = false;
              moreBtn.removeAttribute('aria-busy');
            } else {
              moreBtn.remove();
            }

            var meta = qs(root, '[data-retail-result-meta]');
            if (meta && totalCount) {
              meta.textContent =
                '1–' + shownCount + ' of ' + totalCount + ' results';
            }

            if (qs(root, '[data-retail-kind-pill]') || hasCustomFacets()) {
              applyCustomFiltersDomOnly();
            }
          })
          .catch(function () {
            window.location.href = nextHref;
          })
          .finally(function () {
            root.classList.remove('is-loading');
            if (moreBtn && moreBtn.isConnected) {
              moreBtn.disabled = false;
              moreBtn.removeAttribute('aria-busy');
            }
          });
        return;
      }

      var clear = closest(e.target, '[data-retail-clear-all]');
      if (clear) {
        e.preventDefault();
        e.stopPropagation();
        closeDrawer();
        /* Same destination as the All soho pill: every product, no filters. */
        var allPill = qs(root, '[data-retail-tag-all]');
        var allHref = allPill && allPill.getAttribute('href');
        if (allHref) {
          window.location.assign(allHref);
          return;
        }
        if (hasCustomFacets()) {
          clearCustomFilters();
          return;
        }
        var clearUrl = new URL(getCollectionBasePath(), window.location.origin);
        attachTemplateView(clearUrl);
        clearUrl.searchParams.delete('page');
        window.location.assign(clearUrl.pathname + clearUrl.search);
        return;
      }

      var pill = closest(e.target, '[data-retail-remove-filter]');
      if (pill) {
        e.preventDefault();
        var href = pill.getAttribute('href');
        if (!href) return;
        var removeUrl = new URL(href, window.location.origin);
        removeUrl.searchParams.delete('page');
        fetchSection(removeUrl, true, { scroll: true });
        return;
      }

      var kindPill = closest(e.target, '[data-retail-kind-pill]');
      if (kindPill) {
        e.preventDefault();
        if (kindPill.classList.contains('is-current') || kindPill.classList.contains('is-active')) {
          return;
        }
        setExclusiveKindSelection(kindPill);
        applyCustomFilters();
        return;
      }

      var tagPill = closest(e.target, '[data-retail-tag-pill]');
      if (tagPill) {
        if (
          tagPill.classList.contains('is-current') ||
          tagPill.getAttribute('aria-disabled') === 'true' ||
          tagPill.classList.contains('is-active')
        ) {
          e.preventDefault();
          return;
        }
        /* Full page load so tagged URLs and sibling collections actually swap products.
           Section AJAX with section_id often ignores /collections/handle/tag. */
        return;
      }

      var viewBtn = closest(e.target, '[data-retail-view-size]');
      if (viewBtn) {
        e.preventDefault();
        var size = viewBtn.getAttribute('data-retail-view-size');
        if (!size || viewBtn.classList.contains('is-active')) return;
        root.classList.add('is-loading');
        fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ attributes: { retail_view: String(size) } }),
          credentials: 'same-origin'
        })
          .then(function (res) {
            if (!res.ok) throw new Error('view update failed');
            var nextUrl = new URL(window.location.href);
            nextUrl.searchParams.delete('page');
            return fetchSection(nextUrl, true, { scroll: true });
          })
          .catch(function () {
            root.classList.remove('is-loading');
          });
      }
    });

    window.addEventListener('popstate', function () {
      fetchSection(new URL(window.location.href), false, { scroll: false });
    });

    bindFacetAccordions();
    updateDrawerFacetLabels();
    updateApplyLabel(root.getAttribute('data-result-count') || '0');

    /* If Liquid rendered no cards, load published (Active) products via Ajax API */
    ensurePublishedProductsVisible(root);
  }

  function formatRetailMoney(amount) {
    var cents = Math.round(parseFloat(amount) * 100);
    if (isNaN(cents)) return '';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      var fmt =
        (window.theme && window.theme.moneyFormat) ||
        (window.Shopify && Shopify.money_format) ||
        '£{{amount}}';
      return Shopify.formatMoney(cents, fmt);
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function buildFallbackCard(product) {
    var handle = product.handle;
    var title = product.title || '';
    var href = '/products/' + encodeURIComponent(handle);
    var img =
      (product.images && product.images[0] && (product.images[0].src || product.images[0])) ||
      (product.featured_image && (product.featured_image.src || product.featured_image)) ||
      '';
    var price = product.variants && product.variants[0] ? product.variants[0].price : product.price;
    var available = !!(product.variants || []).some(function (v) {
      return v.available;
    });
    var tags = (product.tags || [])
      .map(function (t) {
        return String(t)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      })
      .filter(Boolean)
      .join(',');
    var priceMajor = '';
    try {
      priceMajor = String(parseFloat(price));
    } catch (e2) {
      priceMajor = '';
    }

    var col = document.createElement('div');
    var isSohoGrid = root.classList.contains('retail-collection--soho');
    col.className = isSohoGrid
      ? 'col-12 col-md-6 col-lg-4 product-item'
      : 'col-12 col-md-6 col-lg-6 col-xl-6 product-item';
    col.setAttribute('data-product-id', String(product.id));
    col.setAttribute('data-retail-tags', tags);
    col.setAttribute('data-retail-price', priceMajor);
    col.setAttribute('data-retail-fallback', '1');
    col.innerHTML =
      '<div class="product-item-v1">' +
      '<div class="product mb-30 engoj_grid_parent relative" data-product-id="' +
      String(product.id) +
      '">' +
      '<div class="img-product relative">' +
      '<div' +
      (available ? '' : ' style="opacity: 0.3"') +
      '>' +
      '<a href="' +
      href +
      '" class="engoj_find_img">' +
      (img
        ? '<img style="width:100%" src="' +
          img +
          '" class="img-responsive" alt="' +
          title.replace(/"/g, '&quot;') +
          '">'
        : '') +
      '</a></div></div>' +
      '<div class="info-product">' +
      '<h4 class="title-product"><a href="' +
      href +
      '">' +
      title.replace(/</g, '&lt;') +
      '</a></h4>' +
      '<div class="price-product"><span class="price">' +
      formatRetailMoney(price) +
      '</span></div>' +
      (available ? '' : '<p class="retail-sold-out-label">Sold out</p>') +
      '</div></div></div>';
    return col;
  }

  function ensurePublishedProductsVisible(root) {
    if (!root || root.__retailFallbackStarted) return;
    var gridRow = qs(root, '[data-retail-results] .product-grid-view .row');
    var existing = qsa(root, '[data-retail-results] .product-item');

    /* Only fallback when the Liquid grid is empty */
    if (!gridRow || existing.length > 0) return;

    var collectionUrl = root.getAttribute('data-collection-url');
    if (!collectionUrl) return;
    root.__retailFallbackStarted = true;

    var empty = qs(root, '[data-retail-empty]');
    if (!empty) {
      empty = document.createElement('div');
      empty.className = 'retail-empty';
      empty.setAttribute('data-retail-empty', '');
      empty.setAttribute('aria-live', 'polite');
      var resultsEl = qs(root, '[data-retail-results]');
      if (resultsEl) resultsEl.appendChild(empty);
    }
    empty.hidden = false;
    empty.innerHTML = '<p>Loading products…</p>';

    fetch(collectionUrl + '/products.json?limit=250', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('products.json ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var products = (data && data.products) || [];
        /* Ajax API only returns Online Store published products (= Active + channel) */
        if (!products.length) {
          empty.hidden = false;
          empty.innerHTML =
            '<p>No Active products in this collection yet. In Admin, set product Status to Active, publish to Online Store, and add the product to this collection. If this is an automated collection, remove any “Inventory greater than 0” condition so sold-out Active products stay listed.</p>';
          return;
        }

        gridRow.innerHTML = '';
        products.forEach(function (product) {
          gridRow.appendChild(buildFallbackCard(product));
        });

        var grid = qs(root, '[data-retail-results] .product-grid-view');
        if (grid) grid.style.display = '';
        empty.hidden = true;

        root.setAttribute('data-result-count', String(products.length));
        root.setAttribute('data-all-count', String(products.length));
        var meta = qs(root, '[data-retail-result-meta]');
        if (meta) {
          meta.textContent = products.length + (products.length === 1 ? ' product' : ' products');
        }
        updateApplyLabelForRoot(root, products.length);
        refreshLazyImages(gridRow);
      })
      .catch(function () {
        empty.hidden = false;
        empty.innerHTML =
          '<p>Could not load products. Confirm each product Status is Active, Online Store is published, and the product is added to this collection.</p>';
      });
  }

  function updateApplyLabelForRoot(root, count) {
    var applyBtn = qs(root, '[data-retail-apply]');
    if (!applyBtn) return;
    var mobileLabel = applyBtn.getAttribute('data-mobile-label');
    if (mobileLabel && window.matchMedia('(max-width: 767.98px)').matches) {
      applyBtn.textContent = mobileLabel;
      return;
    }
    var label = applyBtn.getAttribute('data-label-template') || 'Apply';
    if (label.indexOf('__COUNT__') === -1 && !/\{\{\s*count\s*\}\}/.test(label)) {
      applyBtn.textContent = label;
      return;
    }
    applyBtn.textContent = label
      .replace(/__COUNT__/g, String(count))
      .replace(/\{\{\s*count\s*\}\}/g, String(count));
  }

  function initStandaloneView(root) {
    if (!root || root.__retailViewInit) return;
    root.__retailViewInit = true;
    root.addEventListener('click', function (e) {
      var viewBtn = closest(e.target, '[data-retail-view-size]');
      if (!viewBtn) return;
      e.preventDefault();
      var size = viewBtn.getAttribute('data-retail-view-size');
      if (!size || viewBtn.classList.contains('is-active')) return;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes: { retail_view: String(size) } }),
        credentials: 'same-origin'
      })
        .then(function (res) {
          if (!res.ok) throw new Error('view update failed');
          var nextUrl = new URL(window.location.href);
          nextUrl.searchParams.delete('page');
          window.location.assign(nextUrl.pathname + nextUrl.search + nextUrl.hash);
        })
        .catch(function () {});
    });
  }

  function boot() {
    qsa(document, '[data-retail-collection]').forEach(function (el) {
      if (el.hasAttribute('data-collection-view')) return;
      initCollection(el);
    });
    qsa(document, '[data-collection-view]').forEach(initStandaloneView);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    qsa(e.target, '[data-retail-collection]').forEach(function (el) {
      if (el.hasAttribute('data-collection-view')) return;
      el.__retailCollectionInit = false;
      initCollection(el);
    });
    qsa(e.target, '[data-collection-view]').forEach(function (el) {
      el.__retailViewInit = false;
      initStandaloneView(el);
    });
  });
})();
